/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {Package} from "types/entities/Package";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {useTypedSelector} from "helpers/reducers";
import {ArticleType} from "types/enums/ArticleType";
import {uniqueFilter} from "utils/utility";
import {isWebpSupported} from "react-image-webp/dist/utils";
import {PersonType} from "types/enums/PersonType";
import {useDispatch} from "useDispatch";
import {ThunkAction} from "redux-thunk";
import ShopAction from "store/shop/ShopAction";
import {DEFAULT_HALL_ID} from "helpers/config";
import ArticlesAction from "store/articles/ArticlesAction";
import {filterArticlesByType, useShopItemsCartHook} from "utils/giftCardShopUtils";
import {Article} from "types/entities/Article";
import Loader from "components/Loader";
import {ActivityType} from "types/enums/ActivityType";
import GiftCardArticleCard from "pages/Front/Booking/components/GiftCardPackage/components/GiftCardArticleCard";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import VoucherShoppingCart from "pages/Front/Booking/components/GiftCardPackage/VoucherShoppingCart";

interface GiftCardPackageDetailNewParams {
    package_id: string;
}

interface GiftCardPackageDetailNewProps extends RouteComponentProps<GiftCardPackageDetailNewParams> {
    packageDetail: Package;
}

const YearlyPackageInfo = (props: {
    headerLine: string;
    articleName: string;
    descriptionTitle: string;
    points: string[]
}) => {
    return (
        <div className={"p-4"}>
            <h3 className={"text-2xl font-bold"}>{props.headerLine}</h3>

            <h4 className={"text-xl uppercase"}>{props.articleName}</h4>

            <div className={"italic my-4"}>{props.descriptionTitle}</div>
            <ul className={"list-disc list-inside"}>
                {props.points.map((point, index) => (
                    <li key={index}>{point}</li>
                ))}
            </ul>
        </div>
    );
};
const YearlyPackageDetail = (props: GiftCardPackageDetailNewProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const itemsCart = useTypedSelector(state => state.shopState.itemsCart);
    const session = useTypedSelector(state => state.sessionState.session);
    const articles = useTypedSelector(state => state.articlesState);
    const {addArticleToCart, addCustomGiftToCart} = useShopItemsCartHook();
    const [articlesCount, setArticlesCount] = useState(1);
    const [yearlyArticles, setYearlyArticles] = useState<Article[]>([]);
    const mainImageUrl = isWebpSupported() ? props.packageDetail.main_image_url.replace("jpg", "webp") : props.packageDetail.main_image_url;
    const packageActivities = props.packageDetail.packageItems.map(packageItem => packageItem.activity_id);
    const uniquePackageActivities: number = uniqueFilter(packageActivities);
    const packageActivity = uniquePackageActivities[0] === 1 ? ActivityType.Karting : ActivityType.TheParkPlayground;
    const packageLimit = packageActivity === ActivityType.Karting ? 15 : 10;
    const countChanged = e => {
    };

    const createShopArticleItemsCart = () => {
        dispatch<ThunkAction<any, any, any, any>>(ShopAction.fetchCreateShopArticleItemsCart(DEFAULT_HALL_ID)).then(r => {
            dispatch(ShopAction.resetShoppingCart());
            dispatch(ShopAction.setShopArticleItemsCart(r.shopArticleItemsCart));
        });
    };

    const yearlyArticlesHandler = () => {
        const allArticles = filterArticlesByType(articles.yearly as Article[], ArticleType.Yearly, uniquePackageActivities[0]);
        setYearlyArticles(allArticles);
    };

    const countChangeHandler = (e, personType: PersonType, increment) => {
        switch (personType) {
            case PersonType.Adult:
                if (articlesCount + increment <= packageLimit && articlesCount + increment >= 0) {
                    return setArticlesCount(prev => prev + increment);
                }
                return null;
            default:
                return null;
        }
    };

    useEffect(() => {
        if (
            session &&
            session.access_token
            // && !itemsCart.shopArticleItemsCart
        ) {
            createShopArticleItemsCart();
        }
    }, [session]);

    useEffect(() => {
        if (!itemsCart) {
            return;
        }
    }, [articlesCount]);

    useEffect(() => {
        if (!articles.all || articles.all.length === 0) {
            dispatch(ArticlesAction.fetchArticlesWithHall());
        }
    }, []);

    useEffect(() => {
        if (articles.all) {
            yearlyArticlesHandler();
        }
    }, [articles.all]);

    if (!yearlyArticles) {
        return <Loader />;
    }

    return (
        <div key={props.packageDetail.id}
             className="GiftCardPackageDetailNew flex flex-col lg:flex-row gap-6 font-poppins">
            <div
                className={clsx(
                    "GiftCardPackageDetailNewInfo col-span-8 bg-white shadow-lg rounded-2xl w-full bg-cover bg-no-repeat bg-center",
                    "grid grid-cols-12 gap-4 gap-y-6 sm:gap-y-4 md:gap-y-6 lg:gap-y-8 md:gap-x-6 lg:gap-x-8 xl:gap-x-16 p-4 lg:p-5",
                )}
            >
                <div
                    className={
                        "col-span-12 grid grid-cols-12 gap-4 gap-y-6 sm:gap-y-4 md:gap-x-6 lg:gap-x-6 xl:gap-x-8 md:grid-rows-[0.1fr,1fr] lg:grid-rows-[0.1fr,1fr] xl:grid-rows-[0.2fr,1fr] md:grid-flow-col"
                    }
                >
                    <div
                        className={
                            "MediaSliderHolder mx-auto col-span-12 lg:col-span-4 xl:col-span-4 order-2 md:order-1 md:row-span-2 sm:max-w-md md:max-w-full"
                        }
                    >
                        <div
                            className="w-full relative overflow-hidden select-none rounded-2xl aspect-[64/56] cursor-pointer">
                            <img
                                alt=""
                                className="w-full flex-shrink-0 h-full object-cover object-center select-none pointer-events-none"
                                src="/img/package_category/Jahr.webp"
                            />
                        </div>
                    </div>

                    <div
                        className={"space-y-4 lg:space-y-4 xl:space-y-6 col-span-12 order-1 md:order-2 lg:col-span-8 xl:col-span-8"}>
                        <p className={"font-semibold leading-tight md:leading-normal text-gray-800 text-center md:text-left"}>
                            {t("Constant fun for less money")}
                        </p>
                        <div
                            className={
                                "CategoryTitle font-zuume font-bold italic sm:text-left text-center text-[3rem] sm:text-[3rem] md:text-[4rem] lg:text-[4rem] xl:text-[5rem] tracking-wide leading-[0.85] flex flex-row justify-center md:justify-start space-x-3 sm:space-x-3"
                            }
                        >
                            <span className={"text-arbo-red"}>{t("Arbo")}-</span>
                            <span className={"text-arbo-yellow"}>{t("abo's")}</span>
                        </div>
                    </div>

                    <p className={"col-span-12 lg:col-span-8 xl:col-span-8 order-3 md:row-span-1 text-sm lg:text-base leading-1"}>
                        <div dangerouslySetInnerHTML={{__html: t("With our subscriptions")}} />
                    </p>
                </div>
                <div className={"YearlyGiftCardPackageDetailNewOffer col-span-12 gap-y-6 grid"}>
                    <GiftCardArticleCard articles={yearlyArticles} onClick={article => addArticleToCart(article)}
                                         showDescription={true} />
                </div>
            </div>

            <VoucherShoppingCart isNewStyle={true} withCheckoutButton={true} />
        </div>
    );
};

export default withRouter(YearlyPackageDetail);
